<template>
  <div class="SubordinatesSettings">
    <VSkeletonLoader
      :loading="loading"
      :types="{
        chief: 'card-heading',
        subordinates: 'card-heading@3',
        row: 'chief, subordinates',
        'user-settings': 'row@4'
      }"
      type="user-settings"
    >
      <div v-if="loading">
        <!-- Требуется из-за говнокода в VSkeletonLoader,
             а именно computed-свойство isLoading -->
        Loading...
      </div>
      <VContainer
        v-else
        fluid
        class="grid-table"
      >
        <VRow
          v-if="usersInTable && usersInTable.length"
          class="grid-row"
        >
          <VCol class="grid-col">
            <strong>Руководитель</strong>
          </VCol>
          <VCol
            class="grid-col"
            cols="8"
          >
            <strong>Подчиненные</strong>
          </VCol>
        </VRow>
        <VRow
          v-for="user in usersInTable"
          :key="user.id"
          class="grid-row"
        >
          <VCol class="grid-col d-flex align-center">
            <span>{{ $store.getters['user/displayName'](user.id) }}</span>
            <VBtn
              icon
              :title="
                `Удалить всех подчиненных ${$store.getters['user/displayName'](user.id)}`"
              @click="removeAllSubordinates(user.id)"
            >
              <VIcon>mdi-close</VIcon>
            </VBtn>
          </VCol>
          <VCol
            cols="8"
            class="grid-col"
          >
            <div
              v-for="subId in userIdToSortedSubordinates[user.id]"
              :key="subId"
            >
              <span>{{ $store.getters['user/displayName'](subId) }}</span>
              <VBtn
                icon
                title="Убрать подчиненного"
                @click="removeSubordinate(user.id, subId)"
              >
                <VIcon>mdi-close</VIcon>
              </VBtn>
            </div>
            <VAutocomplete
              v-if="userIdToAutocompleteItems[user.id].length"
              :key="'redraw-seed-' + (userIdToSortedSubordinates[user.id] || ['null']).join('-')"
              :value="null"
              :items="userIdToAutocompleteItems[user.id]"
              item-value="id"
              item-text="displayName"
              hide-details
              :filter="userAutocompleteFilter"
              placeholder="Добавить подчиненного"
              no-data-text="Пользователь не найден"
              class="pt-0"
              @change="addSubordinate(user.id, $event)"
            />
            <i
              v-if="(!userIdToAutocompleteItems[user.id]
                || userIdToAutocompleteItems[user.id].length === 0) &&
                (!userIdToSortedSubordinates[user.id]
                || userIdToSortedSubordinates[user.id].length === 0)"
              class="grey--text"
            >
              (вы &ndash; единственный активный пользователь)
            </i>
          </VCol>
        </VRow>
        <VRow
          v-if="addAutocompleteItems && addAutocompleteItems.length"
          class="grid-row"
        >
          <VCol class="grid-col d-flex align-center">
            <VAutocomplete
              :key="'redraw-seed-' + addAutocompleteItems.map(u => u.id).join('-')"
              :value="null"
              :items="addAutocompleteItems"
              item-value="id"
              item-text="displayName"
              hide-details
              :filter="userAutocompleteFilter"
              label="Добавить руководителя"
              no-data-text="Пользователь не найден"
              @change="addRow($event)"
            />
          </VCol>
        </VRow>
      </VContainer>
    </VSkeletonLoader>
  </div>
</template>

<script>
import * as R from 'ramda'

export default {
  name: 'SubordinatesSettings',
  data() {
    return {
      ui: {
        keepUserIds: [],
      },
    }
  },
  computed: {
    // Все активные пользователи компании
    users() { return this.$store.getters['user/list'] },
    // Поиск пользователя по id
    userMap() { return this.$store.state.user.userMap },
    // Пользователи загружаются
    loading() { return this.users === null },

    // Пользователи с подчиненными
    usersWithRules() {
      const { users } = this
      return users && R.filter(R.compose(
        R.gt(R.__, 0),
        R.length,
        R.prop('subordinates'),
      ))(users)
    },

    // Пользователи с подчиненными + пользователи, у которых были подчиненные,
    // но их удалили и мы оставляем их в таблице, чтобы интерфейс не прыгал
    usersInTable() {
      const { ui: { keepUserIds }, userMap } = this
      return keepUserIds.map(id => userMap[id])
    },

    // Пользователи без подчиненных
    usersWithoutRules() {
      const { users } = this
      return users && R.filter(R.compose(
        R.equals(0),
        R.length,
        R.prop('subordinates'),
      ))(users)
    },

    // Автокомплит добавлений подчиненных, объект, где ключ - id пользователя,
    // значение - список недобавленных пользователей
    userIdToAutocompleteItems() {
      const { $store, usersInTable, users } = this
      return usersInTable.reduce((map, chief) => {
        map[chief.id] = users
          .filter(user => !chief.subordinates.includes(user.id))
          .filter(user => user.id !== chief.id)
          .map(user => ({
            id: user.id,
            displayName: $store.getters['user/displayName'](user.id),
            username: user.username,
          }))
        return map
      }, {})
    },

    // Отсортированные по имени id подчиненных, сгруппированы
    // в объект по id руководителя
    userIdToSortedSubordinates() {
      const { $store, usersWithRules } = this
      return usersWithRules && R.pipe(
        R.map(user => [
          user.id,
          R.sortBy($store.getters['user/displayName'], user.subordinates),
        ]),
        R.fromPairs,
      )(usersWithRules)
    },

    // Автокомплит добавления нового руководителя - пользователи, которых
    // пока нет в таблице
    addAutocompleteItems() {
      const { $store, ui: { keepUserIds }, usersWithoutRules } = this
      return usersWithoutRules
        .filter(u => !keepUserIds.includes(u.id))
        .map(user => ({
          id: user.id,
          displayName: $store.getters['user/displayName'](user.id),
          username: user.username,
        }))
    },
  },
  watch: {
    // при изменении руководителей - запоминаем состояние таблицы
    usersWithRules: {
      handler(usersWithRules) {
        const { $store } = this
        if (!usersWithRules) return
        this.ui.keepUserIds = R.pipe(
          R.map(R.prop('id')),
          R.concat(this.ui.keepUserIds),
          R.uniq,
          R.sortBy($store.getters['user/displayName'])
        )(usersWithRules)
      },
      immediate: true,
    },
  },
  created() {
    this.$store.dispatch('user/getList')
  },
  methods: {
    userAutocompleteFilter(user, queryText) {
      const tokens = queryText.split(/\s+/).map(R.toLower)
      const searchValues = [user.username, user.displayName].map(R.toLower)

      return tokens.every(q =>
        searchValues.some(val =>
          val.includes(q)))
    },
    setSubordinates(userId, subordinateIds) {
      const { $store } = this
      return $store.dispatch('user/setSubordinates', { userId, subordinateIds })
    },
    addSubordinate(userId, subordinateId) {
      const { userMap } = this
      const user = userMap[userId]
      const subordinateIds = R.pipe(
        R.append(subordinateId),
        R.uniq,
      )(user.subordinates)
      return this.setSubordinates(userId, subordinateIds)
    },
    removeSubordinate(userId, subordinateId) {
      const { userMap } = this
      const user = userMap[userId]
      const subordinateIds = user.subordinates
        .filter(id => id !== subordinateId)
      return this.setSubordinates(userId, subordinateIds)
    },
    removeAllSubordinates(userId) {
      this.setSubordinates(userId, [])
      this.ui.keepUserIds = R.without([userId], this.ui.keepUserIds)
    },
    addRow(userId) {
      const { ui: { keepUserIds }, $store } = this
      this.ui.keepUserIds = R.pipe(
        R.append(userId),
        R.uniq,
        R.sortBy($store.getters['user/displayName'])
      )(keepUserIds)
    },
  },
}
</script>

<style lang="sass">
  .SubordinatesSettings
    & .v-skeleton-loader__row
      display: flex
      justify-content: space-between
      padding: 15px 0
      & > .v-skeleton-loader__chief, & > .v-skeleton-loader__subordinates
        flex: 1
        .v-skeleton-loader__heading
          width: 90%
          margin-top: 0px
      & > .v-skeleton-loader__subordinates .v-skeleton-loader__heading
        margin-bottom: 5px

    @function border($size)
      @return ($size) * 1px solid #ddd

    .grid-table
      & .grid-row
        border-top: border(1)
        /*border-bottom: border(1)*/
        /*&:first-child*/
        /*  border-top: border(2)*/
        /*&:last-child*/
        /*  border-bottom: border(2)*/
        /*.grid-col*/
        /*  border-left: border(1)*/
        /*  border-right: border(1)*/
        /*  &:first-child*/
        /*    border-left: border(2)*/
        /*  &:last-child*/
        /*    border-right: border(2)*/
</style>
